/**
 * @file PublishConsentTextsDialog.tsx
 * @description Окно «Опубликовать редакцию N» (phase19.16, Р3г;
 * uiux-r3g-consent-texts §4): что изменено от основы, обязательная заметка
 * «что изменилось» (видна в истории), предупреждение, кого правка коснётся
 * (shared `consentTextsAffect`: виды баннера, в которых видны изменённые
 * поля), и «опубликованную редакцию изменить нельзя». На телефоне — во весь экран.
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { useId, useState } from "react";

import { CONSENT_NOTE_MAX, type ConsentTextShopsReach } from "../../../lib/api-consent-texts";
import { publishWarnings } from "../../../lib/platform/consent-publish-warnings";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Textarea } from "../../ui/textarea";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: ConsentTextKind;
  /** Номер будущей редакции. */
  number: number;
  baseNumber: number;
  /** Отличия от основы — строка «Изменено от ред. N». */
  changed: readonly { field: string; label: string }[];
  /** Отличия от действующей редакции — по ним «кого коснётся» (ревью В1). */
  affected: readonly { field: string }[];
  shops?: ConsentTextShopsReach;
  publishing: boolean;
  /** Сбой публикации — текст полосы с «Повторить». */
  error: string | null;
  onPublish: (note: string) => void;
}

export function PublishConsentTextsDialog(props: Props) {
  const { open, onOpenChange, kind, number, baseNumber, changed, affected, shops, publishing, error, onPublish } = props;
  const id = useId();
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  // Новое открытие — пустая заметка (прошлая не переносится).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setNote("");
      setNoteError(null);
    }
  }

  function submit() {
    if (note.trim() === "") {
      setNoteError("Напишите, что изменилось — это увидят в истории редакций");
      return;
    }
    onPublish(note);
  }

  const title = `Опубликовать редакцию ${number}`;
  return (
    <Dialog open={open} onOpenChange={(next) => (!publishing ? onOpenChange(next) : undefined)}>
      <DialogContent fullScreen className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {changed.length > 0 ? `Изменено от ред. ${baseNumber}: ${changed.map((c) => c.label).join(", ")}` : `Без отличий от ред. ${baseNumber}`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-note`}>Что изменилось * (видно в истории)</Label>
          <Textarea
            id={`${id}-note`}
            rows={2}
            value={note}
            maxLength={CONSENT_NOTE_MAX}
            aria-invalid={!!noteError}
            aria-describedby={`${id}-note-meta${noteError ? ` ${id}-note-error` : ""}`}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteError(null);
            }}
          />
          <span id={`${id}-note-meta`} className="type-caption self-end tabular-nums text-on-surface-variant">
            {[...note].length}/{CONSENT_NOTE_MAX}
          </span>
          {noteError ? (
            <p id={`${id}-note-error`} className="type-caption text-destructive">
              {noteError}
            </p>
          ) : null}
        </div>
        {publishWarnings(kind, affected, number, shops).map((text) => (
          <NoticeStrip key={text} tone="warning">
            {text}
          </NoticeStrip>
        ))}
        <NoticeStrip tone="info">Опубликованную редакцию изменить нельзя — только выпустить новую.</NoticeStrip>
        {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
        <DialogFooter>
          <Button variant="ghost" className="min-h-12" disabled={publishing} onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button variant="brand" className="min-h-12" loading={publishing} onClick={submit}>
            {error ? "Повторить" : title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
